import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useIntl } from "react-intl";
import styled from "styled-components";

const StyledInput = styled(Form.Control)`
  width: 120px;
  text-align: right;
`;

interface PriceInputProps {
  value: number | undefined;
  onChange: (value: number) => void;
  placeholder?: string;
  className?: string;
}

export const PriceInput: React.FC<PriceInputProps> = ({
  value,
  onChange,
  placeholder,
  className,
}) => {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const decimalSeparator = useMemo(() => {
    const formatted = new Intl.NumberFormat(intl.locale).format(1.1);
    return formatted.charAt(1);
  }, [intl.locale]);

  const formatPrice = useCallback(
    (num: number): string => {
      return intl.formatNumber(num, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    [intl]
  );

  const localePlaceholder = useMemo(() => {
    if (placeholder) return placeholder;
    return formatPrice(0);
  }, [placeholder, formatPrice]);

  useEffect(() => {
    if (!isEditing && value !== undefined) {
      setEditValue(formatPrice(value));
    }
  }, [value, isEditing, formatPrice]);

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    // Set value with locale-specific decimal separator for easier editing
    if (value !== undefined) {
      const valueStr = value.toString().replace(".", decimalSeparator);
      setEditValue(valueStr);
    } else {
      setEditValue("");
    }
  }, [value, decimalSeparator]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      if (inputValue === "") {
        setEditValue("");
        return;
      }

      // Allow digits and both comma and dot as decimal separators
      const regex = /^(\d*)[,.]?(\d*)$/;
      const match = inputValue.match(regex);
      if (!match) {
        return;
      }

      const [, integerPart, decimalPart] = match;
      if (inputValue.includes(",") || inputValue.includes(".")) {
        setEditValue(
          decimalPart !== undefined
            ? `${integerPart}${decimalSeparator}${decimalPart}`
            : `${integerPart}${decimalSeparator}`
        );
      } else {
        setEditValue(integerPart);
      }
    },
    [decimalSeparator]
  );

  const handleBlur = useCallback(() => {
    setIsEditing(false);

    const normalizedValue = editValue.replace(decimalSeparator, ".");
    const numValue = normalizedValue === "" ? 0 : parseFloat(normalizedValue);
    const validatedValue = isNaN(numValue) || numValue < 0 ? 0 : numValue;

    // Round to 2 decimals to ensure the emitted value has exactly 2 decimal places
    const finalValue = Math.round(validatedValue * 100) / 100;

    setEditValue(formatPrice(finalValue));
    onChange(finalValue);
  }, [editValue, decimalSeparator, formatPrice, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent minus sign
    if (e.key === "-" || e.key === "Subtract") {
      e.preventDefault();
    }

    // Submit on Enter
    if (e.key === "Enter") {
      inputRef.current?.blur();
    }
  }, []);

  const displayValue = isEditing ? editValue : value !== undefined ? formatPrice(value) : "";

  return (
    <StyledInput
      ref={inputRef}
      type="text"
      placeholder={localePlaceholder}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={className}
      autoComplete="off" // disable autocomplete to prevent browser suggestions interfering with input
      data-1p-ignore // disable 1pass autocomplete to prevent browser suggestions interfering with input
    />
  );
};
